import * as React from 'react'
import { Button, Checkbox, FormField, IOption, Input, Label, Modal, Select } from 'uxp/components';
import { FAIcon } from '../components/FAIcon';
import classNames from 'classnames';
import { Conditional } from '../ConditionalComponent';
import { FAIcon as FontAwesomeIcon } from '../components/FAIcon';
import { generateUUID, hasProperty, hasValue, loadScriptFileAsync, tryParseJSON } from '../../../util';
import { WidgetDesignerPillInput } from "../pill-input/PillInput";
import jp from 'jsonpath'
import { cloneDeep } from 'lodash-es'
import {
    CustomEventProps,
    CustomEventType,
    IAction,
    IActionEditorLinkProps,
    IActionStep,
    IActionType,
    IAddOnProps,
    IRedirectAddOn,
    IRedirectWidget,
    IWidgetProps,
} from './action-types';
import {
    ActionTypeIcons,
    ActionTypes,
    DefaultCustomEvent,
    DefaultLucyAction,
    DefaultRedirectAddon,
    DefaultRedirectUrl,
    DefaultRedirectWidget,
} from './action-defaults';
import { extractActionSteps } from './action-utils';
import { ActionParameterEditor } from './ActionParameterEditor';
import { $L } from '../../../localize';
import { LucyActionEditor } from './LucyActionEditor';
import './ActionEditor.scss';

const ActionEditorLink: React.FunctionComponent<IActionEditorLinkProps> = (props) => {
    let { disableAction, disableUrl, disableWidget, disableActionParameters, disableAddOn, disableMobileUI, disableTriggerEvent } = props.settings || {};

    let [isOpen, setIsOpen] = React.useState(false);
    let [action, setAction] = React.useState<IAction>({
        type: 'open-url',
        redirectUrl: { url: 'about:blank', target: "_blank" }
    })
    let [currentStepPath, setCurrentStepPath] = React.useState<string>('$')
    let [widgets, setWidgets] = React.useState<IWidgetProps[]>([]);
    let [installedAddOns, setInstalledAddOns] = React.useState<IAddOnProps[]>([]);
    const [events, setEvents] = React.useState<CustomEventType[]>([
        { label: $L('widgetdesigner.actions.event-overlay-status', 'Widget Overlay Status Event'), value: 'dt-viewer-status-event', params: { isReady: false } },
        {
            label: $L('widgetdesigner.actions.event-overlay-change', 'Widget Overlay Change Event'), value: 'dt-view-change-event', params: {
                objectId: '',
                objectType: '',
                layoutId: '',
            }
        },
    ])

    let hasMounted = React.useRef(false)
    let currentStepPathRef = React.useRef(null)

    React.useEffect(() => {
        hasMounted.current = true;
        return () => {
            hasMounted.current = false
        }
    }, [])

    React.useEffect(() => {
        if (hasMounted.current) {
            let act = props.action
            if (!act?.type || act.type.trim().length == 0) {
                act = { type: 'open-url', redirectUrl: { url: 'about:blank' } }
            }
            setAction(act)
        }
    }, [props.action])

    React.useEffect(() => {
        if (currentStepPathRef.current) {
            setCurrentStepPath(currentStepPathRef.current)
        }
        else if (!currentStepPath) {
            let steps = extractActionSteps(action)
            if (steps.length > 0) setCurrentStepPath(steps[0].basePath)
        }
    }, [action])

    React.useEffect(() => {
        getAllWidgets();
        getInstalledAddOns();
    }, [props.uxpContext]);

    function getName(widget: any) {
        if (hasValue(widget?.name)) return widget.name
        else if (hasValue(widget?.title)) return widget.title
        else if (hasValue(widget?.label)) return widget.label
        else return 'unknown'
    }
    function mapToWidgetOption(w: any): IWidgetProps {
        const name = getName(w)
        return {
            name: name,
            id: w.id,
            sourceUrl: w.sourceUrl,
            key: `${w.id}###${name.toLowerCase()}`,
            isTemplate: !!w.isTemplate,
            templateType: w.templateType,
            templateKey: w.templateKey,
            // CardTemplate record guid — needed by CardTemplate:DetailsById at drilldown
            // time. Backend field first, envelope fallback for older backends.
            templateId: w.templateId || w.props?.__template__?.id || null
        } as IWidgetProps
    }

    async function getAllWidgets() {
        // Same source as the dashboard widget drawer: installed components + designer-saved
        // card templates. Marketplace-only entries carry installed:false and are excluded.
        try {
            const res = await fetch('/api/uxp/search-components', {
                method: 'POST',
                headers: { 'Authorization': props.uxpContext?.apiKey ? `APIKEY ${props.uxpContext.apiKey}` : '' }
            })
            if (!res.ok) throw new Error(`search-components failed: ${res.status}`)
            const components = await res.json()
            const widgets = (Array.isArray(components) ? components : [])
                .filter((c: any) => c.installed === true && (c.modes || ['widget']).includes('widget'))
            setWidgets(widgets.map(mapToWidgetOption))
        }
        catch (e) {
            console.log('exception getting components, falling back to GetWidgets', e);
            // older backends without SearchComponents — previous behavior
            props.uxpContext?.executeService('UXP', 'GetWidgets', { pageNumber: 0, max: 100000 }, { json: true })
                .then((res) => {
                    let data = res[0]
                    let widgets = tryParseJSON(data.widgets, [])
                    setWidgets(widgets.map(mapToWidgetOption));
                })
                .catch(e => {
                    console.log('exception getting widgets', e);
                })
        }
    }

    function getInstalledAddOns() {
        props.uxpContext?.executeService('Lucy', 'InstalledUseCase:InstalledUseCases', { max: 100, last: 0 }, { json: true }).then((data: IAddOnProps[]) => {
            setInstalledAddOns(data);


        }).catch((e) => {

        });
    }
    function getNameAndPath(action: IAction) {
        if (action.type == 'open-url') {
            return { name: 'Open URL', path: 'redirectUrl' }
        }
        if (action.type == 'open-widget') {
            return { name: 'Open Widget', path: 'redirectWidget' }
        }
        return { name: 'Execute Action', path: 'lucyAction' }
    }

    function renderBreadCrum() {
        let breadcrumbSteps: Array<IActionStep> = extractActionSteps(action)

        if (breadcrumbSteps.length < 2) return null
        return <div className="breadcrumb">
            {breadcrumbSteps.map((b, i) => {
                let active = b.basePath == currentStepPath
                return <div className={classNames("breadcrumb-step", { 'active': active })}
                    key={i}
                    onClick={() => { setCurrentStepPath(b.basePath) }}
                >
                    {b.name}
                </div>
            })}
        </div>
    }


    function visibleActionTypes(currentType: IActionType) {
        return ActionTypes.filter((a) => {
            if (disableUrl && a.value == 'open-url') return false;
            if (disableWidget && a.value == 'open-widget') return false;
            if (disableAction && a.value == 'execute-action') return false;
            if (disableAddOn && a.value == 'open-addon') return false;
            if (disableMobileUI && a.value == 'open-mobile-ui') return false;
            if (disableTriggerEvent && a.value == 'trigger-custom-event') return false;
            // mobile-app-only types (no web runtime handler) — hidden unless a
            // legacy action already uses one, so it stays editable
            if ((a.value == 'open-addon' || a.value == 'open-mobile-ui') && currentType !== a.value) return false;
            return true
        })
    }

    // picker-style category sidebar — reflects/sets the type of the CURRENT step
    function renderSidebar() {
        let steps = extractActionSteps(action)
        let step = steps.find(s => s.basePath == currentStepPath) || steps[0]
        if (!step) return null

        let _action: IAction = jp.value(action, step.basePath)
        let currentType = _action?.type || 'open-url'

        return <div className="uxp_wd-dsp__sidebar">
            <div className="uxp_wd-dsp__sidebar-label">{$L('widgetdesigner.actions.action-type', 'Action Type')}</div>
            {visibleActionTypes(currentType).map((a) => (
                <div
                    key={a.value}
                    className={'uxp_wd-dsp__sidebar-item' + (currentType === a.value ? ' uxp_wd-dsp__sidebar-item--selected' : '')}
                    onClick={() => {
                        jp.apply(action, `${step.basePath}.type`, () => (a.value))
                        props.onChange(action)
                    }}
                >
                    <FAIcon className="uxp_wd-dsp__sidebar-icon" icon={ActionTypeIcons[a.value] || ['fas', 'link']} />
                    <span className="uxp_wd-dsp__sidebar-text">{$L(`widgetdesigner.actions.type-${a.value}`, a.label)}</span>
                </div>
            ))}
        </div>
    }

    // back navigation for nested steps (type selection lives in the sidebar)
    function renderStepHeader(step: IActionStep) {
        if (!step.previousPath) return null

        return <div className="uxp_wd-axn-editor__stephead">
            <div className="back-btn"
                onClick={(e) => {
                    let parent = (e.currentTarget.parentNode).parentNode;
                    (parent as HTMLDivElement).classList.add('remove')

                    setTimeout(() => {
                        setCurrentStepPath(step.previousPath)
                    }, 200);
                }}
            >
                <FontAwesomeIcon icon={['fas', 'chevron-left']} />
            </div>
            <span className="uxp_wd-axn-editor__stephead-name">{step.name}</span>
        </div>
    }

    function updateValue(_action: IAction, path: string, value: any, basePath: string) {
        jp.apply(_action, path, () => (value))
        if (basePath == '$') action = _action
        else jp.apply(action, basePath, () => (_action))
        props.onChange(action);
    }

    function renderUrlEditor(step: IActionStep) {
        let _action: IAction = jp.value(action, step.basePath)
        if (_action?.type !== 'open-url') return null

        // fill missing values
        _action = { ..._action, redirectUrl: { ...cloneDeep(DefaultRedirectUrl), ..._action?.redirectUrl } }

        return <React.Fragment>

            <FormField>
                <Label className="uxp_wd-axn-editor__field">{$L('widgetdesigner.actions.url-label', 'Url to redirect to')}</Label>
                <WidgetDesignerPillInput
                    value={_action?.redirectUrl?.url || ''}
                    onChange={(v) => { updateValue(_action, '$.redirectUrl.url', v, step.basePath) }}
                    className="uxp_wd-axn-editor__field"
                    placeholder={$L('widgetdesigner.actions.url-placeholder', 'Ex: http://lucyinthesky.io/')}
                    contextData={props.contextData}
                    showFields={true}
                />
            </FormField>
            <FormField>
                <Label className='uxp_wd-axn-editor__field'>{$L('widgetdesigner.actions.open-in', 'Open in')}</Label>
                <Select
                    selected={_action?.redirectUrl?.target || '_blank'}
                    options={[
                        { label: $L('widgetdesigner.actions.new-tab', 'New tab'), value: '_blank' },
                        { label: $L('widgetdesigner.actions.current-tab', 'Current tab'), value: '_self' }
                    ]}
                    onChange={v => { updateValue(_action, '$.redirectUrl.target', v, step.basePath) }}
                    className="uxp_wd-axn-editor__field"
                />
            </FormField>
        </React.Fragment>
    }

    function renderExecuteAction(step: IActionStep) {

        let _action: IAction = jp.value(action, step.basePath)
        if (_action?.type !== 'execute-action') return null

        // fill missing values
        if (!hasProperty(_action, '$.lucyAction')) {
            _action.lucyAction = { ...cloneDeep(DefaultLucyAction) }
        }
        _action = { ..._action, lucyAction: { ...cloneDeep(DefaultLucyAction), ..._action?.lucyAction } }

        return <div className='slider'>
            <div className='lucy-action-section'>
                <LucyActionEditor
                    uiProps={props.uiProps}
                    designer={props.designer}
                    value={_action.lucyAction}
                    onChange={(v) => { updateValue(_action, '$.lucyAction', v, step.basePath) }}
                    uxpContext={props.uxpContext}
                    contextData={props.contextData}
                    disableParameters={!!disableActionParameters}
                />
            </div>
            <div className='dialog-section'>
                <FormField>
                    <Label className="uxp_wd-axn-editor__field">{$L('widgetdesigner.actions.dialog-title', 'Dialog Title')}</Label>
                    <Input
                        value={_action.lucyAction.dialogTitle || ''}
                        onChange={(v) => { updateValue(_action, '$.lucyAction.dialogTitle', v, step.basePath) }}
                        className="uxp_wd-axn-editor__field"
                        placeholder={$L('widgetdesigner.actions.dialog-title-placeholder', 'Dialog title')}
                    />
                </FormField>
                <FormField>
                    <Label className="uxp_wd-axn-editor__field">{$L('widgetdesigner.actions.execution-message', 'Execution Message')}</Label>
                    <Input
                        value={_action.lucyAction?.executionMsg || ''}
                        onChange={(v) => { updateValue(_action, '$.lucyAction.executionMsg', v, step.basePath) }}
                        className="uxp_wd-axn-editor__field"
                        placeholder={$L('widgetdesigner.actions.execution-message', 'Execution Message')}
                    />
                </FormField>

                <FormField>
                    <Label className="uxp_wd-axn-editor__section-label">{$L('widgetdesigner.actions.on-success', 'On success')}</Label>

                    <FormField>
                        <Label className="uxp_wd-axn-editor__field">{$L('widgetdesigner.actions.completed-message', 'Completed Message')}</Label>
                        <Input
                            value={_action.lucyAction.postExecution.success.msg || ''}
                            onChange={(v) => { updateValue(_action, '$.lucyAction.postExecution.success.msg', v, step.basePath) }}
                            className="uxp_wd-axn-editor__field"
                            placeholder={$L('widgetdesigner.actions.completed-message', 'Completed Message')}
                        />
                    </FormField>

                    <FormField>
                        <Label className="uxp_wd-axn-editor__field">{$L('widgetdesigner.actions.button-label', 'Button Label')}</Label>
                        <Input
                            value={_action.lucyAction.postExecution.success.buttonTitle || ''}
                            onChange={(v) => { updateValue(_action, '$.lucyAction.postExecution.success.buttonTitle', v, step.basePath) }}
                            className="uxp_wd-axn-editor__field"
                            placeholder={$L('widgetdesigner.common.done', 'Done')}
                        />
                    </FormField>

                    {/* goToHome / goBack were v4 mobile-only (screen stack) — removed on web */}
                    <FormField>
                        <Checkbox
                            type='bordered'
                            checked={_action.lucyAction.postExecution.success.reloadWidget}
                            onChange={(v) => { updateValue(_action, '$.lucyAction.postExecution.success.reloadWidget', v, step.basePath) }}
                            className="uxp_wd-axn-editor__field"
                            label={$L('widgetdesigner.actions.reload-widget', 'Reload the widget on click the button')}
                        />
                    </FormField>

                    {/* <FormField>
                        <Checkbox
                            checked={_action.lucyAction.postExecution.success.goToHome || false}
                            onChange={(v) => { updateValue(_action, '$.lucyAction.postExecution.success.goToHome', v, step.basePath) }}
                            label="Go To Home Page"
                            className="uxp_wd-axn-editor__field"

                        />
                    </FormField>

                    <Conditional visible={!_action.lucyAction.postExecution.success.goToHome}>
                        <FormField>
                            <Label className="uxp_wd-axn-editor__field">Button Action</Label>
                            <Button title='Edit Action' onClick={() => {
                                setCurrentStepPath(step.basePath + '.lucyAction.postExecution.success.action')
                            }} />
                        </FormField>
                    </Conditional> */}

                </FormField>

                <FormField>
                    <Label className="uxp_wd-axn-editor__section-label">{$L('widgetdesigner.actions.on-error', 'On error')}</Label>

                    <FormField>
                        <Label className="uxp_wd-axn-editor__field">{$L('widgetdesigner.actions.error-message', 'Error Message')}</Label>
                        <Input
                            value={_action.lucyAction.postExecution.error.msg || ''}
                            onChange={(v) => { updateValue(_action, '$.lucyAction.postExecution.error.msg', v, step.basePath) }}
                            className="uxp_wd-axn-editor__field"
                            placeholder={$L('widgetdesigner.actions.error-message', 'Error Message')}
                        />
                    </FormField>

                    <FormField>
                        <Label className="uxp_wd-axn-editor__field">{$L('widgetdesigner.actions.button-label', 'Button Label')}</Label>
                        <Input
                            value={_action.lucyAction.postExecution.error.buttonTitle || ''}
                            onChange={(v) => { updateValue(_action, '$.lucyAction.postExecution.error.buttonTitle', v, step.basePath) }}
                            className="uxp_wd-axn-editor__field"
                            placeholder={$L('widgetdesigner.common.done', 'Done')}
                        />
                    </FormField>

                    {/* goToHome / goBack were v4 mobile-only (screen stack) — removed on web */}
                    <FormField>
                        <Checkbox
                            type='bordered'
                            checked={_action.lucyAction.postExecution.error.reloadWidget}
                            onChange={(v) => { updateValue(_action, '$.lucyAction.postExecution.error.reloadWidget', v, step.basePath) }}
                            className="uxp_wd-axn-editor__field"
                            label={$L('widgetdesigner.actions.reload-widget', 'Reload the widget on click the button')}
                        />
                    </FormField>

                    {/* <FormField>
                        <Checkbox
                            checked={_action.lucyAction.postExecution.error.goToHome || false}
                            onChange={(v) => { updateValue(_action, '$.lucyAction.postExecution.error.goToHome', v, step.basePath) }}
                            label="Go To Home Page"
                            className="uxp_wd-axn-editor__field"

                        />
                    </FormField>

                    <Conditional visible={!_action.lucyAction.postExecution.error.goToHome}>
                        <FormField>
                            <Label className="uxp_wd-axn-editor__field">Button Action</Label>
                            <Button title='Edit Action' onClick={() => {

                                if (!hasProperty(_action, '$.lucyAction.postExecution.error.action')) {
                                    let _defaultAction: IAction = { type: 'open-url' }
                                    currentStepPathRef.current = step.basePath + '.lucyAction.postExecution.error.action'
                                    updateValue(_action, '$.lucyAction.postExecution.error.action', _defaultAction, step.basePath)
                                }
                                else {
                                    setCurrentStepPath(step.basePath + '.lucyAction.postExecution.error.action')
                                }
                            }} />
                        </FormField>
                    </Conditional> */}

                </FormField>

            </div>
        </div>
    }

    async function onSelectAWidget(widget: IWidgetProps) {
        return new Promise(async (done, nope) => {
            let registered = (window as any).Widgets || []
            let instance = registered.find((r: any) => r.id == widget.id)
            if (!instance) {
                await loadScriptFileAsync(widget.sourceUrl)
            }
            instance = registered.find((r: any) => r.id == widget.id)
            if (!instance) {
                console.log('Unable to find the widget even after loading the module');
                return done({})
            }

            // get the props
            let props = instance?.configs?.props || []
            let defaultProps = instance?.defaultProps || {}

            let _props: { [key: string]: any } = {}
            props.map((p: any) => {
                if (p.name != 'uiProps') {
                    _props[p.name] = defaultProps[p.name] || ''
                }
            })
            done(_props)
        })
    }

    function renderPageEditor(step: IActionStep) {
        let _action: IAction = jp.value(action, step.basePath)
        if (_action?.type !== 'open-widget') return null

        if (!hasProperty(_action, '$.redirectWidget')) {
            _action.redirectWidget = { ...cloneDeep(DefaultRedirectWidget) }
        }
        _action = { ..._action, redirectWidget: { ...cloneDeep(DefaultRedirectWidget), ..._action?.redirectWidget } }

        const _widgets = [...widgets]
        if (!_widgets.find(w => w.key === _action.redirectWidget?.widget?.key)) {
            const newItem = {
                id: _action.redirectWidget?.widget?.key,
                name: _action.redirectWidget?.widget?.key,
                key: _action.redirectWidget?.widget?.key,
                isTemplate: false,
                sourceUrl: null,

            } as IWidgetProps
            _widgets.unshift(newItem)
        }

        return <React.Fragment>
            <FormField>
                <Label className="uxp_wd-axn-editor__field">{$L('widgetdesigner.actions.select-page', 'Select a page')}</Label>
                <Select
                    selected={_action.redirectWidget?.widget?.key || ''}
                    options={widgets}
                    labelField="name"
                    valueField="key"
                    onChange={(v, o) => {

                        onSelectAWidget(o)
                            .then(props => {
                                let _redirectwidget: IRedirectWidget = {
                                    widget: o,
                                    params: props || {}
                                }
                                updateValue(_action, '$.redirectWidget', _redirectwidget, step.basePath)
                            })
                    }}
                    className="uxp_wd-axn-editor__field"
                    placeholder={$L('widgetdesigner.actions.select-widget-placeholder', 'Select a Widget')}
                    addNewValues={{
                        enable: true,
                        title: $L('widgetdesigner.actions.add-value', 'Add Value'),
                        loadingTitle: $L('widgetdesigner.actions.adding', 'Adding...'),
                        onAddNewValue: (value) => {
                            return new Promise<any>((done, nope) => {
                                const newItem = { name: value, id: value, key: value, isTemplate: false, sourceUrl: null } as IWidgetProps
                                setWidgets(prev => {
                                    prev.push(newItem)
                                    return prev
                                })
                                setTimeout(() => { done(newItem) }, 1000)
                            })
                        },
                    }}
                />
            </FormField>

            <FormField>
                <ActionParameterEditor
                    designer={props.designer}
                    uiProps={props.uiProps}
                    value={_action.redirectWidget.params || {}}
                    onChange={(v) => { updateValue(_action, '$.redirectWidget.params', v, step.basePath) }}
                    contextData={props.contextData}
                />
            </FormField>
        </React.Fragment>;
    }

    function renderAddOnEditor(step: IActionStep) {
        let _action: IAction = jp.value(action, step.basePath)
        if (_action?.type !== 'open-addon') return null

        if (!hasProperty(_action, '$.redirectAddOn')) {
            _action.redirectAddOn = { ...cloneDeep(DefaultRedirectAddon) }
        }
        _action = { ..._action, redirectAddOn: { ...cloneDeep(DefaultRedirectAddon), ..._action?.redirectAddOn } }

        return <React.Fragment>
            <FormField>
                <Label className="uxp_wd-axn-editor__field">{$L('widgetdesigner.actions.select-addon', 'Select a Add-on')}</Label>
                <Select
                    selected={_action.redirectAddOn?.addon?.Key || ''}
                    options={installedAddOns}
                    labelField="Name"
                    valueField="Key"
                    onChange={(v, o) => {


                        let _redirectAddOn: IRedirectAddOn = {
                            addon: o
                        }
                        updateValue(_action, '$.redirectAddOn', _redirectAddOn, step.basePath)
                    }}
                    className="uxp_wd-axn-editor__field"
                    placeholder={$L('widgetdesigner.actions.select-addon-placeholder', 'Select a Addon')}
                />
            </FormField>


        </React.Fragment>;
    }

    function renderMobileUIEditor(step: IActionStep) {

        let _action: IAction = jp.value(action, step.basePath)
        if (_action?.type !== 'open-mobile-ui') return null

        if (!_action.hasOwnProperty('mobileUI')) _action.mobileUI = ''

        const options: { label: string, value: string }[] = [
            { value: 'settings', label: $L('widgetdesigner.panels.settings', 'Settings') },
            { value: 'feeds', label: $L('widgetdesigner.actions.mobile-news-feeds', 'News Feeds') },
            { value: 'qrview', label: $L('widgetdesigner.actions.mobile-qr', 'QR Code Scanner') },
            { value: 'current_items', label: $L('widgetdesigner.actions.mobile-notifications', 'Notifications') },
            { value: 'notifications', label: $L('widgetdesigner.actions.mobile-notifications', 'Notifications') },
            { value: 'currency_screen', label: $L('widgetdesigner.actions.mobile-currency', 'Currency') },
            { value: 'language_screen', label: $L('widgetdesigner.actions.mobile-language', 'Language') },
        ]

        if (hasValue(_action?.mobileUI)) {
            const opt = options.find(op => op.value == _action?.mobileUI)
            if (!opt) {
                options.push({ label: _action.mobileUI, value: _action.mobileUI })
            }
        }

        return <React.Fragment>

            <Label>{$L('widgetdesigner.actions.mobile-note', 'Note: This will only work in the mobile app')}</Label>

            <FormField>

                {/* <AutoCompleteInput
                    value={_action.mobileUI || ''}
                    onChange={s => {
                        updateValue(_action, '$.mobileUI', s, step.basePath)
                    }}
                    className='uxp_wd-axn-editor__field'
                    options={['Qr Code Scanner', 'Settings Page']}
                /> */}

                <Select
                    selected={_action.mobileUI || ''}
                    onChange={s => {
                        updateValue(_action, '$.mobileUI', s, step.basePath)
                    }}
                    className='uxp_wd-axn-editor__field'
                    options={options}
                    addNewValues={{
                        enable: true,
                        title: $L('widgetdesigner.actions.use-this-value', 'Use this value'),
                        loadingTitle: $L('widgetdesigner.actions.updating', 'Updating...'),
                        onAddNewValue: async (value) => {
                            updateValue(_action, '$.mobileUI', value, step.basePath)
                            return true
                        }
                    }}
                />
            </FormField>
        </React.Fragment>
    }

    function getEventParams(eventId: string) {
        const eventType = events.find(e => e.value == eventId)
        return eventType ? eventType?.params || {} : {}
    }
    function renderTriggerCustomEvent(step: IActionStep) {

        let _action: IAction = jp.value(action, step.basePath)
        if (_action?.type !== 'trigger-custom-event') return null

        if (!hasProperty(_action, '$.customEvent')) {
            _action.customEvent = { ...DefaultCustomEvent }
        }

        if (!_action?.customEvent?.params) {
            _action.customEvent.params = getEventParams(_action?.customEvent?.eventId)
        }

        _action = Object.assign(DefaultCustomEvent, _action)

        return <React.Fragment>
            <FormField>
                <Label className="uxp_wd-axn-editor__field">{$L('widgetdesigner.actions.select-event', 'Select an Event')} </Label>
                <Select
                    selected={_action?.customEvent?.eventId || ''}
                    options={events}
                    onChange={(v, o) => {

                        const event: CustomEventProps = {
                            eventId: v,
                            params: o?.params || {}
                        }
                        updateValue(_action, '$.customEvent',event, step.basePath)
                    }}
                    className="uxp_wd-axn-editor__field"
                    placeholder={$L('widgetdesigner.actions.select-event', 'Select an Event')}
                    addNewValues={{
                        enable: true,
                        title: $L('widgetdesigner.actions.add-event', 'Add Event'),
                        loadingTitle: $L('widgetdesigner.actions.adding', 'Adding...'),
                        onAddNewValue: (value) => {
                            return new Promise<any>((done, nope) => {
                                const newItem: IOption = { label: value, value: value }
                                setEvents(prev => {
                                    prev.push(newItem)
                                    return prev
                                })
                                setTimeout(() => { done(newItem) }, 1000)
                            })
                        },
                    }}
                />
            </FormField>

            <FormField>
                <ActionParameterEditor
                    designer={props.designer}
                    uiProps={props.uiProps}
                    value={_action.customEvent.params || {}}
                    onChange={(v) => { updateValue(_action, '$.customEvent.params', v, step.basePath) }}
                    contextData={props.contextData}
                />
            </FormField>
        </React.Fragment>;
    }

    function renderContent(s: IActionStep, i: number) {
        let currentIndex = extractActionSteps(action).findIndex(s => s.basePath == currentStepPath)

        // render only up to the current step
        if (i > currentIndex) return null

        return <div className={classNames('action-editor-step')}>
            {renderStepHeader(s)}
            {renderUrlEditor(s)}
            {renderExecuteAction(s)}
            {renderPageEditor(s)}
            {renderAddOnEditor(s)}
            {renderMobileUIEditor(s)}
            {renderTriggerCustomEvent(s)}
        </div>
    }

    function done() {
        if (props.changeOnClose) props.onChange(action)
        if (hasMounted.current) {
            setIsOpen(false)
        }
    }

    return <React.Fragment>
        <Button title={$L('widgetdesigner.actions.edit-action', 'Edit Action')} onClick={() => setIsOpen(true)} className="active" />

        <Modal
            show={isOpen}
            onClose={done}
            title={$L('widgetdesigner.actions.editor-title', 'Action Editor')}
            className="uxp_wd-axn-editor-modal"
            closeButton={<Button title={$L('widgetdesigner.common.done', 'Done')} onClick={done} />}
            backgroundDismiss={false}
        >
            <div className="uxp_wd-dsp uxp_wd-axn-editor">
                <div className="uxp_wd-dsp__layout">
                    {renderSidebar()}
                    <div className="uxp_wd-axn-editor__pane">
                        <div className="content">
                            {
                                extractActionSteps(action).map((s, i) => (renderContent(s, i)))
                            }
                        </div>
                    </div>
                </div>
            </div>
        </Modal>

    </React.Fragment>
}

export default ActionEditorLink
